import {
  addDays,
  endOfWeek,
  format,
  isSameDay,
  parse,
  startOfToday,
  startOfWeek,
} from "date-fns";

/**
 * Parses a date string in the format "yyyy-MM-dd" into a Date object.
 * @param {string | undefined | null} dateValue - The date string to be parsed.
 * If the input is `undefined` or `null`, the function returns `null`.
 * @returns {Date | null} A Date object if the parsing is successful; otherwise, `null`.
 */
export const parseDateString = (
  dateValue: string | undefined | null
): Date | null => {
  if (dateValue === null || dateValue === undefined) return null;
  // Parse the date string into a Date object
  const parsedDate = parse(dateValue, "yyyy-MM-dd", new Date());
  if (isNaN(parsedDate.getTime())) return null;
  return parsedDate;
};

/**
 * Converts a given Date object into a formatted string in the "yyyy-MM-dd" format.
 * If the input is null or undefined, the function returns undefined.
 * @param {Date | null | undefined} date - The date object to format. Can be null or undefined.
 * @returns {string | undefined} A string representing the formatted date, or undefined if the input is null or undefined.
 */
export const formatDateToString = (
  date: Date | null | undefined
): string | undefined => {
  return date ? format(date, "yyyy-MM-dd") : undefined;
};

/**
 * Checks if the given start and end dates exactly match the specified date range.
 *
 * This function compares the provided `start` and `end` dates with the `rangeStart`
 * and `rangeEnd` dates. If `start` and `end` are not null and both dates match
 * their respective range boundaries, it returns true. Otherwise, it returns false.
 * @param {Date | null} start - The start date to compare; can be null.
 * @param {Date | null} end - The end date to compare; can be null.
 * @param {Date} rangeStart - The starting boundary of the date range.
 * @param {Date} rangeEnd - The ending boundary of the date range.
 * @returns {boolean} True if the `start` and `end` dates are not null and exactly
 * match `rangeStart` and `rangeEnd`, respectively, otherwise false.
 */
export const isExactDateRangeMatch = (
  start: Date | null,
  end: Date | null,
  rangeStart: Date,
  rangeEnd: Date
): boolean => {
  return (
    start !== null &&
    end !== null &&
    isSameDay(start, rangeStart) &&
    isSameDay(end, rangeEnd)
  );
};

/**
 * Returns an object containing date ranges for the current day, the current week, and the next week.
 * @returns {object} An object with the following properties:
 *   - today: The start of the current day.
 *   - thisWeekStart: The start of the current week.
 *   - thisWeekEnd: The end of the current week.
 *   - nextWeekStart: The start of the next week.
 *   - nextWeekEnd: The end of the next week.
 */
export const getDateRanges = (): {
  today: Date;
  thisWeekStart: Date;
  thisWeekEnd: Date;
  nextWeekStart: Date;
  nextWeekEnd: Date;
} => {
  const today = startOfToday();

  return {
    today,
    thisWeekStart: startOfWeek(today),
    thisWeekEnd: endOfWeek(today),
    nextWeekStart: startOfWeek(addDays(today, 7)),
    nextWeekEnd: endOfWeek(addDays(today, 7)),
  };
};

/**
 * Determines the matching date range category for the provided start and end date.
 * @function getMatchingDateRange
 * @param {Date | null} start - The start date of the range, or null if not specified.
 * @param {Date | null} end - The end date of the range, or null if not specified.
 * @returns {object} An object indicating the matching date range category:
 * - isPast: {boolean} True if the end date is today and the start date is null.
 * - isToday: {boolean} True if the range exactly matches today's date.
 * - isThisWeek: {boolean} True if the range exactly corresponds to the current week.
 * - isNextWeek: {boolean} True if the range exactly corresponds to the next week.
 */
export const getMatchingDateRange = (
  start: Date | null,
  end: Date | null
): {
  isPast: boolean;
  isToday: boolean;
  isThisWeek: boolean;
  isNextWeek: boolean;
} => {
  const { today, thisWeekStart, thisWeekEnd, nextWeekStart, nextWeekEnd } =
    getDateRanges();

  return {
    isPast: end !== null && isSameDay(end, today) && start === null,
    isToday: isExactDateRangeMatch(start, end, today, today),
    isThisWeek: isExactDateRangeMatch(start, end, thisWeekStart, thisWeekEnd),
    isNextWeek: isExactDateRangeMatch(start, end, nextWeekStart, nextWeekEnd),
  };
};
